import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { ClientStore } from 'src/app/stores/stores';
import { Observable, Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { StoresState } from '../../stores/+state/stores.reducer';
import { getSelectedStore } from '../../stores/+state/stores.selectors';
import { getSelectedStore as getSelectedStorePublic } from '../../public/store/+state/stores.selectors';
import { takeUntil } from 'rxjs/operators';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
} from '@angular/forms';
import StoreUtils from 'src/app/public/store/utils/StoreUtils';

@Component({
  selector: 'app-price-input',
  templateUrl: './price-input.component.html',
  styleUrls: ['./price-input.component.scss'],
})
export class PriceInputComponent implements OnInit, OnDestroy, OnChanges {
  selectedStore$: Observable<ClientStore>;
  selectedStorePublic$: Observable<ClientStore>;
  selectedStandalonePayment$: Observable<ClientStore>;
  priceSeparator: any;
  currencyPosition: number;
  selectedStoreCurrencySymbol: string;
  private destroy$ = new Subject<void>();
  @Input() priceFormControl: FormControl;
  @Input() allowNegValue = false;
  @Input() showCurrencySymbol = true;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() isRequired = false;
  @Input() emptyAsNull = false;
  @Input() hideErrors = false;
  // Optional automation testid prefix. When set, the whole-part input gets
  // `${priceTestId}-price` and the decimal-part input gets `${priceTestId}-decimal`.
  // Left null for existing usages so their markup is unchanged.
  @Input() priceTestId: string | null = null;
  @Output() priceChange = new EventEmitter<void>();
  priceInputForm: FormGroup;

  constructor(
    private store: Store<StoresState>,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    if (this.priceFormControl) {
      const price = this.priceFormControl.value;
      const shouldInitializeAsEmpty =
        this.emptyAsNull &&
        (price === null || price === undefined || price === '');
      this.priceInputForm = this.fb.group({
        priceVal: [shouldInitializeAsEmpty ? '' : '0'],
        priceDecimalVal: [
          shouldInitializeAsEmpty ? '' : '00',
          Validators.compose([
            Validators.maxLength(4),
            Validators.pattern('^[0-9]*$'),
          ]),
        ],
      });
      this.priceInputForm.patchValue({
        priceVal:
          price != null && price.toString().split('.')[0]
            ? price.toString().split('.')[0]
            : shouldInitializeAsEmpty
              ? ''
              : 0,
        priceDecimalVal:
          price != null && price.toString().split('.')[1]
            ? price.toString().split('.')[1]
            : shouldInitializeAsEmpty
              ? ''
              : '00',
      });

      // Set up validators based on explicit isRequired property
      const baseValidators = [Validators.maxLength(128)];
      const patternValidator = Validators.pattern(
        this.allowNegValue
          ? '^0|(-?[1-9][0-9]{0,5})$'
          : '^0|([1-9][0-9]{0,5})$',
      );

      if (this.isRequired) {
        this.getControl('priceVal').setValidators([
          ...baseValidators,
          Validators.required,
          patternValidator,
        ]);
      } else {
        this.getControl('priceVal').setValidators([
          ...baseValidators,
          patternValidator,
        ]);
      }

      this.priceFormControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((val) => {
          if (
            this.emptyAsNull &&
            (val === null || val === undefined || val === '')
          ) {
            if (this.priceInputForm.get('priceVal').value !== '') {
              this.priceInputForm.get('priceVal').setValue('');
            }
            if (this.priceInputForm.get('priceDecimalVal').value !== '') {
              this.priceInputForm.get('priceDecimalVal').setValue('');
            }
            return;
          }
          if (val != null && val != this.priceInputForm.get('priceVal').value) {
            this.priceInputForm
              .get('priceVal')
              .setValue(
                val && val.toString().split('.')[0]
                  ? val.toString().split('.')[0]
                  : 0,
              );
          }

          if (
            val != null &&
            val.toString().split('.')[1] &&
            val.toString().split('.')[1] !=
              this.priceInputForm.get('priceDecimalVal').value
          ) {
            this.priceInputForm
              .get('priceDecimalVal')
              .setValue(
                val && val.toString().split('.')[1]
                  ? val.toFixed(2).toString().split('.')[1]
                  : null,
              );
          }
        });
    }
    this.preparePriceDisplayFormat();

    if (this.disabled) {
      this.setDisabledState(this.disabled);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      this.setDisabledState(changes['disabled'].currentValue);
    }
  }

  private setDisabledState(isDisabled: boolean) {
    if (!this.priceInputForm) return;
    if (isDisabled) {
      this.priceInputForm.disable({ emitEvent: false });
    } else {
      this.priceInputForm.enable({ emitEvent: false });
    }
  }

  emitDataOnUpdateValue(e) {
    if (e.target.value || e.target.value === '') {
      if (
        e.target.id === 'priceVal' &&
        e.target.value.length > 1 &&
        e.target.value.startsWith('0')
      ) {
        const newValue = e.target.value.substring(1);
        this.getControl('priceVal').setValue(newValue);
        e.target.value = newValue;
      }

      this.priceFormControl.markAsTouched();

      const priceVal = this.getControl('priceVal').value;
      const priceDecimalVal = this.getControl('priceDecimalVal').value;
      const isCompletelyEmpty =
        (priceVal === null || priceVal === undefined || priceVal === '') &&
        (priceDecimalVal === null ||
          priceDecimalVal === undefined ||
          priceDecimalVal === '');
      const isEmpty =
        (!priceVal || priceVal === '0' || priceVal === 0) &&
        (!priceDecimalVal || priceDecimalVal === '00');

      if (this.emptyAsNull && !this.isRequired && isCompletelyEmpty) {
        this.priceFormControl.setErrors(null);
        this.priceFormControl.setValue(null);
        this.priceChange.emit();
        return;
      }

      if (this.priceInputForm.valid) {
        const decimalVal = this.getControl('priceDecimalVal').value || '00';
        const finalPrice = Number(
          this.getControl('priceVal').value + '.' + decimalVal,
        );
        if (this.priceFormControl) {
          this.priceFormControl.setValue(finalPrice);
          this.priceChange.emit();
        }
      } else if (!this.isRequired && isEmpty) {
        this.priceFormControl.setErrors(null);
        this.priceFormControl.setValue(null);
        this.priceChange.emit();
      } else {
        this.priceFormControl.setErrors({ invalid: true });
      }
    } else {
      if (!this.isRequired) {
        this.priceFormControl.setErrors(null);
        this.priceFormControl.setValue(null);
      } else {
        this.priceFormControl.setErrors({ invalid: true });
      }
    }
  }

  getControl(name: string) {
    return this.priceInputForm.get(name);
  }

  shouldShowRequiredError(controlName: string): boolean {
    const control = this.getControl(controlName);
    return (
      this.isRequired &&
      control.errors?.required &&
      (control.dirty || control.touched)
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  preparePriceDisplayFormat() {
    let selectedStore: ClientStore;
    let selectedStoreLocale: string;
    let selectedStoreCurrency: string;

    this.selectedStorePublic$ = this.store.pipe(select(getSelectedStorePublic));
    this.selectedStorePublic$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        selectedStore = value;
        if (selectedStore && selectedStore.address && selectedStore.currency) {
          selectedStoreLocale = selectedStore.locale;
          selectedStoreCurrency = selectedStore.currency.isoCode;
          this.selectedStoreCurrencySymbol =
            selectedStoreCurrency === 'XCD'
              ? 'XCD'
              : selectedStore.currency.symbol;
          const price = 0.0;

          const formattedPrice = price
            .toLocaleString(selectedStoreLocale, {
              style: 'currency',
              currency: selectedStoreCurrency,
              currencyDisplay: 'code',
              useGrouping: false,
            })
            .replace(selectedStoreCurrency, this.selectedStoreCurrencySymbol);
          this.priceSeparator =
            formattedPrice.indexOf(',') > 0 || formattedPrice.indexOf('٫') > 0
              ? ','
              : '.';
          this.currencyPosition = formattedPrice.indexOf(
            this.selectedStoreCurrencySymbol,
          );
        }
      });

    this.selectedStore$ = this.store.pipe(select(getSelectedStore));
    this.selectedStore$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      selectedStore = value;
      if (
        selectedStore &&
        selectedStore.address &&
        selectedStore.currency &&
        selectedStore.currency.isoCode
      ) {
        selectedStoreLocale = StoreUtils.getStoreLocale(selectedStore);
        selectedStoreCurrency = StoreUtils.getCurrencyIsoCode(selectedStore);
        this.selectedStoreCurrencySymbol =
          StoreUtils.getCurrencySymbol(selectedStore);
        const price = 0.0;
        const formattedPrice = price
          .toLocaleString(selectedStoreLocale, {
            style: 'currency',
            currency: selectedStoreCurrency,
            currencyDisplay: 'code',
            useGrouping: false,
          })
          .replace(selectedStoreCurrency, this.selectedStoreCurrencySymbol);
        this.priceSeparator =
          formattedPrice.indexOf(',') > 0 || formattedPrice.indexOf('٫') > 0
            ? ','
            : '.';
        this.currencyPosition = formattedPrice.indexOf(
          this.selectedStoreCurrencySymbol,
        );
      }
    });
  }

  reset(): void {
    this.priceInputForm.patchValue({
      priceVal: this.emptyAsNull ? '' : 0,
      priceDecimalVal: this.emptyAsNull ? '' : '00',
    });
  }

  onInputFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      inputElement.select();
    }
  }
}
